import {
  Box,
  Button,
  Container,
  Divider,
  FormLabel,
  Grid,
  Autocomplete,
  Paper,
  Stack,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import { Months } from "../../../../utils";
import useAdd from "../../../../api/useAdd";
import { useTranslation } from "react-i18next";
import EmployeeLeaveList from "./EmployeeLeaveList";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1; // Months are 0-indexed
const years = Array.from(
  { length: currentYear - 2020 + 1 },
  (_, i) => 2020 + i,
);

const EmployeeLeave = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [formData, setFormData] = useState({
    employee: "",
    month: currentMonth,
    year: currentYear,
    description: "",
    days: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const { data: employeeData } = useFetchObjects(["users"], "users", token);

  const { handleAdd, resetForm, loading } = useAdd(
    "employee-leave",
    token,
    t("hr.employeeLeaveSuccess"),
    t("hr.failedEmployeeLeave"),
  );

  useEffect(() => {
    if (resetForm) {
      setFormData({
        employee: "",
        month: currentMonth,
        year: currentYear,
        description: "",
        days: "",
      });
      setFormErrors({});
      setSuccessMessage("");
      setErrorMessage("");
    }
  }, [resetForm]);

  const handleSave = async (e) => {
    e.preventDefault();
    let errors = {};

    // Validate required fields
    if (!formData.employee) {
      errors.employee = t("hr.employeeRequired");
    }
    if (!formData.month) {
      errors.month = t("hr.monthRequired");
    }
    if (!formData.year) {
      errors.year = t("hr.yearRequired");
    }
    if (!formData.description) {
      errors.description = t("hr.descriptionRequired");
    }
    if (!formData.days) {
      errors.days = t("hr.daysRequired");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
    } else {
      const success = handleAdd(formData);
      if (success) {
        setFormData({
          employee: "",
          month: currentMonth,
          year: currentYear,
          description: "",
          days: "",
        });
        setFormErrors({});
      } else {
        setFormErrors(errors);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" });
  };

  return (
    <Container>
      <Paper
        sx={{
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          marginTop: "2rem",
          marginBottom: "2rem",
          padding: "2rem",
        }}
      >
        <Typography fontWeight={600} variant="h4" color="primary" gutterBottom>
          {t("hr.leaveForm")}
        </Typography>
        <Divider sx={{ mt: 2, mb: 2, width: "100%" }} />

        {successMessage && (
          <Typography color="green" variant="body1" gutterBottom>
            {successMessage}
          </Typography>
        )}
        {errorMessage && (
          <Typography color="red" variant="body1" gutterBottom>
            {errorMessage}
          </Typography>
        )}

        <form onSubmit={handleSave}>
          <Grid container spacing={3}>
            {/* Employee Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.employee")}</FormLabel>
              </Box>
              <Autocomplete
                options={employeeData}
                getOptionLabel={(option) =>
                  `${option.first_name} ${option.last_name}`
                }
                onChange={(event, value) => {
                  setFormData({ ...formData, employee: value ? value.id : "" });
                  setFormErrors({ ...formErrors, employee: "" });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    fullWidth
                    label={t("hr.selectEmployee")}
                    error={!!formErrors.employee}
                    helperText={formErrors.employee}
                  />
                )}
              />
            </Grid>

            {/* Month Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.month")}</FormLabel>
              </Box>
              <TextField
                select
                size="small"
                name="month"
                value={formData.month}
                label={t("hr.selectMonth")}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                error={!!formErrors.month}
                helperText={formErrors.month}
              >
                {Months.map((monthOption) => (
                  <MenuItem key={monthOption.id} value={monthOption.id}>
                    {monthOption.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Year Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.year")}</FormLabel>
              </Box>
              <TextField
                select
                size="small"
                name="year"
                value={formData.year}
                label={t("hr.selectYear")}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                error={!!formErrors.year}
                helperText={formErrors.year}
              >
                {years.map((yearOption) => (
                  <MenuItem key={yearOption} value={yearOption}>
                    {yearOption}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Days Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.days")}</FormLabel>
              </Box>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                type="number"
                name="days"
                value={formData.days}
                label={t("hr.insertDays")}
                onChange={handleChange}
                error={!!formErrors.days}
                helperText={formErrors.days}
              />
            </Grid>

            {/* Description Field */}
            <Grid item xs={12}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t("hr.description1")}</FormLabel>
              </Box>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                error={!!formErrors.description}
                helperText={formErrors.description}
              />
            </Grid>
          </Grid>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Button variant="contained" type="submit" disabled={loading}>
              {t("hr.save")}
            </Button>
          </Stack>
        </form>

        <Box mt={3}>
          <Divider />
          <EmployeeLeaveList />
        </Box>
      </Paper>
    </Container>
  );
};

export default EmployeeLeave;
